import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import {
  ThemeProvider,
  BaseStyles,
  TextInput,
  FormControl,
  Checkbox,
  CheckboxGroup,
  Select,
} from "@primer/react";
import { SearchIcon } from "@primer/octicons-react";
import { handleEventClick } from "./calendarTooltip";
import {
  StatusCheckboxes,
  computeOperatingHours,
  filterEventsByStatus,
  statusValues,
} from "./facilityFilters";

const TYPE_LABELS = {
  Venue: "Venues",
  Equipment: "Equipment",
  Consumable: "Consumables",
  Service: "Services",
};

const ZOOM_LEVELS = [
  { value: "30min", label: "30 Min" },
  { value: "hours", label: "Hours" },
  { value: "quarter", label: "AM / PM" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

function MatrixFilters({ filters, onChange, facilityTypes, requestStatuses }) {
  const debouncedSearch = useDebounce(
    useCallback((value) => onChange((prev) => ({ ...prev, search: value })), [onChange]),
    300
  );

  return (
    <ThemeProvider>
      <BaseStyles style={{ background: "transparent" }}>
        <div className="d-flex flex-column" style={{ gap: "16px" }}>
          <FormControl>
            <FormControl.Label>Search</FormControl.Label>
            <TextInput
              type="search"
              placeholder="Search by title..."
              defaultValue={filters.search}
              onChange={(e) => debouncedSearch(e.target.value)}
              leadingVisual={SearchIcon}
              block
              size="large"
            />
          </FormControl>

          {facilityTypes.length > 1 && (
            <FormControl>
              <FormControl.Label>Facility Type</FormControl.Label>
              <Select
                value={filters.facilityType}
                onChange={(e) => onChange((prev) => ({ ...prev, facilityType: e.target.value }))}
                block
                size="large"
              >
                {facilityTypes.map((t) => (
                  <Select.Option key={t} value={t}>
                    {TYPE_LABELS[t] || t}
                  </Select.Option>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl>
            <FormControl.Label>Zoom</FormControl.Label>
            <Select
              value={filters.zoom}
              onChange={(e) => onChange((prev) => ({ ...prev, zoom: e.target.value }))}
              block
              size="large"
            >
              {ZOOM_LEVELS.map((z) => (
                <Select.Option key={z.value} value={z.value}>
                  {z.label}
                </Select.Option>
              ))}
            </Select>
          </FormControl>

          <StatusCheckboxes
            statuses={requestStatuses}
            selected={filters.statuses}
            onChange={(statuses) => onChange((prev) => ({ ...prev, statuses }))}
          />

          <CheckboxGroup>
            <CheckboxGroup.Label>Display</CheckboxGroup.Label>
            <FormControl>
              <Checkbox
                checked={filters.weekends}
                onChange={(e) => onChange((prev) => ({ ...prev, weekends: e.target.checked }))}
              />
              <FormControl.Label>Show Weekends</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.businessHours}
                onChange={(e) => onChange((prev) => ({ ...prev, businessHours: e.target.checked }))}
              />
              <FormControl.Label>Show Business Hours</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.includeArchived}
                onChange={(e) => onChange((prev) => ({ ...prev, includeArchived: e.target.checked }))}
              />
              <FormControl.Label>Show Archived / Cancelled</FormControl.Label>
              <FormControl.Caption>Bookings on archived events and cancelled bookings, which no longer hold their facilities.</FormControl.Caption>
            </FormControl>
          </CheckboxGroup>
        </div>
      </BaseStyles>
    </ThemeProvider>
  );
}

export function FacilitiesMatrix({
  eventsUrl,
  timezone,
  facilities = [],
  facilityTypes = [],
  facilityUrlTemplate,
  requestStatuses = [],
}) {
  const calendarRef = useRef(null);
  const defaultType = facilityTypes.includes("Venue") ? "Venue" : facilityTypes[0] || "";
  const [filters, setFilters] = useState({
    search: "",
    facilityType: defaultType,
    zoom: "quarter",
    weekends: true,
    businessHours: true,
    includeArchived: false,
    statuses: statusValues(requestStatuses),
  });

  const filteredFacilities = facilities.filter((f) => {
    if (filters.facilityType && f.type !== filters.facilityType) return false;
    return true;
  });

  // GR-3081: sortIndex preserves the server's manual (position) order —
  // FullCalendar's resourceOrder otherwise defaults to sorting rows by id.
  const resources = filteredFacilities.map((f, index) => ({
    id: String(f.id),
    title: f.name,
    sortIndex: index,
    extendedProps: { capacity: f.capacity, type: f.type },
  }));

  // Compute earliest open / latest close from filtered facilities' operating hours
  const { earliestOpen, latestClose, businessHours } = computeOperatingHours(filteredFacilities);

  const fetchEvents = useCallback(
    (fetchInfo, successCallback, failureCallback) => {
      const url = new URL(eventsUrl, window.location.origin);
      url.searchParams.set("start", fetchInfo.startStr);
      url.searchParams.set("end", fetchInfo.endStr);
      if (filters.facilityType) url.searchParams.set("facility_type", filters.facilityType);
      if (filters.search) url.searchParams.set("search", filters.search);
      // GR-4399: bookings on archived events and cancelled bookings are left off unless asked for.
      if (filters.includeArchived) url.searchParams.set("include_archived", "true");

      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((data) => successCallback(filterEventsByStatus(data.events, filters.statuses, requestStatuses)))
        .catch(failureCallback);
    },
    [eventsUrl, filters, requestStatuses]
  );

  // GR-3145: a closure is a background stripe, so FullCalendar gives it no click
  // target and paints its title in the layer beneath the booking chips sharing
  // the lane — the two collided and neither read. The stripe carries the meaning
  // on a capacity view; the reason moves to a hover title, and the label itself
  // is hidden in custom-calendar.scss.
  const closureHoverTitle = useCallback((info) => {
    const props = info.event.extendedProps || {};
    if (props.type !== "blocked_date") return;

    const closed = props.facility ? `${props.facility} closed` : "Closed";
    info.el.title = [closed, props.reason].filter(Boolean).join(": ");
  }, []);

  const resourceLabelContent = useCallback((arg) => {
    if (facilityUrlTemplate) {
      const url = facilityUrlTemplate.replace(/%7BfacilityId%7D|{facilityId}/gi, arg.resource.id);
      const a = document.createElement("a");
      a.href = url;
      a.className = "color-fg-default";
      a.textContent = arg.resource.title;
      return { domNodes: [a] };
    }
    return arg.resource.title;
  }, [facilityUrlTemplate]);

  // Refetch events when status filters or blocked dates toggle change
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) api.refetchEvents();
  }, [filters.statuses, filters.includeArchived]);

  const zoom = filters.zoom;
  const SLOT_DURATIONS = {
    "30min": "00:30:00",
    hours: "01:00:00",
    quarter: "06:00:00",
    daily: { days: 1 },
    weekly: { weeks: 1 },
  };
  const slotDuration = SLOT_DURATIONS[zoom] || "01:00:00";
  const filtersPortal = document.getElementById("matrix-filters-portal");

  return (
    <div className="admin-events-calendar">
      {filtersPortal &&
        createPortal(
          <MatrixFilters
            filters={filters}
            onChange={setFilters}
            facilityTypes={facilityTypes}
            requestStatuses={requestStatuses}
          />,
          filtersPortal
        )}
      <FullCalendar
        ref={calendarRef}
        plugins={[resourceTimelinePlugin]}
        initialView="resourceTimelineWeek"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "resourceTimelineDay,resourceTimelineWeek",
        }}
        buttonText={{
          today: "Today",
          day: "Day",
          week: "Week",
        }}
        resources={resources}
        events={fetchEvents}
        eventClick={handleEventClick}
        eventDidMount={closureHoverTitle}
        timeZone={timezone || "local"}
        height="auto"
        resourceLabelContent={resourceLabelContent}
        resourceOrder="sortIndex"
        resourceAreaHeaderContent="Facility"
        resourceAreaWidth="180px"
        slotDuration={slotDuration}
        slotLabelFormat={
          zoom === "weekly"
            ? [{ week: "short" }]
            : zoom === "daily"
              ? [{ weekday: "short", month: "numeric", day: "numeric" }]
              : zoom === "quarter"
                ? [
                    { weekday: "short", month: "numeric", day: "numeric" },
                    { hour: "numeric" },
                  ]
                : [
                    { weekday: "short", month: "numeric", day: "numeric" },
                    { hour: "numeric", meridiem: "short", omitZeroMinute: true },
                  ]
        }
        slotLabelContent={zoom === "quarter"
          ? (arg) => {
              if (arg.level === 0) return arg.text;
              // FullCalendar marker dates hold the calendar-timezone wall clock in
              // their UTC fields; use getUTCHours so the bucket matches the rendered
              // slot regardless of the browser's timezone.
              return arg.date.getUTCHours() < 12 ? "Morning" : "Afternoon";
            }
          : undefined
        }
        weekends={filters.weekends}
        slotMinTime={zoom === "daily" || zoom === "weekly" ? "00:00:00" : earliestOpen}
        slotMaxTime={zoom === "daily" || zoom === "weekly" ? "24:00:00" : latestClose}
        businessHours={zoom === "daily" || zoom === "weekly" || !filters.businessHours ? false : businessHours}
        nowIndicator
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
      />
    </div>
  );
}
